import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  userApi,
  auditLogApi,
  importLogApi,
  storageConfigApi,
  syncConfigApi,
  bulkActionApi,
} from "../adminApi";
import { queryKeys } from "./queryKeys";
import useInvalidatingMutation from "./useInvalidatingMutation";

/* ---------- Users ---------- */
export const useUsers = () =>
  useQuery({ queryKey: queryKeys.users.all, queryFn: userApi.getAll });
// Server-paged user list (["users", "page", params] → refreshed with every users.all invalidation)
export const useUserPage = (params) =>
  useQuery({
    queryKey: [...queryKeys.users.all, "page", params],
    queryFn: () => userApi.getPage(params),
    placeholderData: keepPreviousData,
  });

/** Employees an account may link to (same company and branch, free). */
export const useLinkableEmployees = (params, enabled = true) =>
  useQuery({
    queryKey: [...queryKeys.users.all, "linkable", params],
    queryFn: () => userApi.getLinkableEmployees(params),
    enabled: enabled && !!params?.companyId,
    placeholderData: keepPreviousData,
  });

export const useSaveUser = () =>
  useInvalidatingMutation((user) => userApi.save(user), [queryKeys.users.all]);

export const useDeleteUser = () =>
  useInvalidatingMutation((id) => userApi.remove(id), [queryKeys.users.all]);

/* ---------- Audit logs ---------- */
export const useAuditLogs = (params) =>
  useQuery({
    queryKey: queryKeys.auditLogs.list(params),
    queryFn: () => auditLogApi.getAll(params),
    placeholderData: keepPreviousData,
  });

export const useAuditModules = () =>
  useQuery({
    queryKey: ["auditLogs", "modules"],
    queryFn: auditLogApi.getModules,
    staleTime: 30 * 60 * 1000,
  });

export const useAuditLog = (id) =>
  useQuery({
    queryKey: ["auditLogs", "detail", id],
    queryFn: () => auditLogApi.getById(id),
    enabled: !!id,
  });

/* ---------- Import history ---------- */
export const useImportHistory = (params = {}) =>
  useQuery({
    queryKey: queryKeys.importLogs.history(params),
    queryFn: () => importLogApi.getHistory(params),
  });

export const useImportBatchDetails = (batchId) =>
  useQuery({
    queryKey: queryKeys.importLogs.batch(batchId),
    queryFn: () => importLogApi.getBatchDetails(batchId),
    enabled: !!batchId,
  });

/* ---------- Storage configs ---------- */
/** scope: "company" (the user's company) or "all" (global list) */
export const useStorageConfigs = (scope, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.storageConfigs.list(scope),
    queryFn:
      scope === "company"
        ? storageConfigApi.getForCompany
        : storageConfigApi.getAll,
    enabled,
  });

export const useStorageTemplate = (provider) =>
  useQuery({
    queryKey: queryKeys.storageConfigs.template(provider),
    queryFn: () => storageConfigApi.getTemplate(provider),
    enabled: !!provider,
    staleTime: Infinity,
  });

export const useCreateStorageConfig = () =>
  useInvalidatingMutation(
    (data) => storageConfigApi.create(data),
    [queryKeys.storageConfigs.all],
  );

export const useUpdateStorageConfigValue = () =>
  useInvalidatingMutation(
    (args) => storageConfigApi.updateValue(args),
    [queryKeys.storageConfigs.all],
  );

/* ---------- Scheduled syncs ---------- */
export const useSyncConfigs = () =>
  useQuery({
    queryKey: queryKeys.syncConfigs.all,
    queryFn: syncConfigApi.getAll,
  });

export const useCreateSyncConfig = () =>
  useInvalidatingMutation(
    (dto) => syncConfigApi.create(dto),
    [queryKeys.syncConfigs.all],
  );

export const useUpdateSyncConfig = () =>
  useInvalidatingMutation(
    ({ id, dto }) => syncConfigApi.update(id, dto),
    [queryKeys.syncConfigs.all],
  );

export const useDeleteSyncConfig = () =>
  useInvalidatingMutation(
    (id) => syncConfigApi.remove(id),
    [queryKeys.syncConfigs.all],
  );

/** A company's branches, their Zoho folders and each folder's latest run. */
export const useSyncOverview = (companyId) =>
  useQuery({
    queryKey: ["syncOverview", companyId],
    queryFn: () => syncConfigApi.overview(companyId),
    enabled: !!companyId,
    retry: false,
  });

const SYNC_POLL_MS = 5000;

/** A task's runs. Polls while one is running, or until pollUntil (just after Run Now). */
export const useSyncRuns = (id, params, { pollUntil = 0 } = {}) =>
  useQuery({
    queryKey: ["syncRuns", id, params],
    queryFn: () => syncConfigApi.runs(id, params),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.data?.some((r) => r.status === "IN_PROGRESS") ||
      Date.now() < pollUntil
        ? SYNC_POLL_MS
        : false,
  });

/** Files of a run. revision (the run's status) reloads them once it finishes. */
export const useSyncRunFiles = (id, runId, params, { revision, live } = {}) =>
  useQuery({
    queryKey: ["syncRuns", id, "files", runId, params, revision],
    queryFn: () => syncConfigApi.runFiles(id, runId, params),
    placeholderData: keepPreviousData,
    refetchInterval: live ? SYNC_POLL_MS : false,
  });

export const useRunSync = () =>
  useInvalidatingMutation(
    (id) => syncConfigApi.run(id),
    [queryKeys.syncConfigs.all, ["syncRuns"], ["syncOverview"]],
  );

/* ---------- Bulk actions ---------- */
export const useTriggerBulkSync = () =>
  useInvalidatingMutation((payload) => bulkActionApi.triggerSync(payload), []);
